import React, { createContext, useState, useContext } from 'react';
import { firestore, timestamp } from '../firebase/config';
import { collection, addDoc, updateDoc, doc, getDoc, getDocs, query, where, deleteDoc } from 'firebase/firestore';

// Création du contexte des réservations
const BookingContext = createContext();

// Hook personnalisé pour utiliser le contexte des réservations
export const useBooking = () => {
  return useContext(BookingContext);
};

// Fournisseur du contexte des réservations
export const BookingProvider = ({ children }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fonction pour récupérer toutes les réservations
  const getBookings = async () => {
    try {
      setLoading(true);
      const bookingsCollection = collection(firestore, 'bookings');
      const bookingsSnapshot = await getDocs(bookingsCollection);
      const bookingsList = bookingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBookings(bookingsList);
      setLoading(false);
      return bookingsList;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Fonction pour récupérer une réservation par ID
  const getBookingById = async (id) => {
    try {
      const bookingDoc = await getDoc(doc(firestore, 'bookings', id));
      if (bookingDoc.exists()) {
        return { id: bookingDoc.id, ...bookingDoc.data() };
      } else {
        return null;
      }
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour créer une nouvelle réservation
  const createBooking = async (bookingData) => {
    try {
      const newBooking = {
        ...bookingData,
        status: 'pending',
        createdAt: timestamp(),
        updatedAt: timestamp()
      };
      const docRef = await addDoc(collection(firestore, 'bookings'), newBooking);
      return { id: docRef.id, ...newBooking };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour mettre à jour une réservation
  const updateBooking = async (id, bookingData) => {
    try {
      const bookingRef = doc(firestore, 'bookings', id);
      const updatedData = {
        ...bookingData,
        updatedAt: timestamp()
      };
      await updateDoc(bookingRef, updatedData);
      return { id, ...updatedData };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour annuler une réservation
  const cancelBooking = async (id, reason) => {
    try {
      const bookingRef = doc(firestore, 'bookings', id);
      await updateDoc(bookingRef, {
        status: 'cancelled',
        cancellationReason: reason,
        cancelledAt: timestamp(),
        updatedAt: timestamp()
      });
      return { id, status: 'cancelled', cancellationReason: reason };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour obtenir les statistiques des réservations
  const getBookingStats = async () => {
    try {
      const bookingsList = await getBookings();
      
      const stats = {
        total: bookingsList.length,
        confirmed: 0,
        pending: 0,
        cancelled: 0,
        totalRevenue: 0
      };
      
      bookingsList.forEach(booking => {
        if (booking.status === 'confirmed') {
          stats.confirmed++;
          stats.totalRevenue += booking.totalAmount || 0;
        } else if (booking.status === 'pending') {
          stats.pending++;
        } else if (booking.status === 'cancelled') {
          stats.cancelled++;
        }
      });
      
      return stats;
    } catch (error) {
      setError(error.message);
      return {
        total: 0,
        confirmed: 0,
        pending: 0,
        cancelled: 0,
        totalRevenue: 0
      };
    }
  };

  // Valeur du contexte
  const value = {
    bookings,
    loading,
    error,
    getBookings,
    getBookingById,
    createBooking,
    updateBooking,
    cancelBooking,
    getBookingStats
  };

  return (
    <BookingContext.Provider value={value}>
      {children}
    </BookingContext.Provider>
  );
};

export { BookingContext };
